import { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from './ApiContext';
import { QueryErrorBoundary } from '../components/QueryErrorBoundary';

const AppDataContext = createContext();

async function fetchTextData(API) {
  const res = await API.post('/query?action=execute&dataSource=rvcustdatascope', {
    query: "form = 'DPTexts'",
    maxScanDocs: 500000,
    maxScanEntries: 200000,
    timeoutSecs: 300,
    viewRefresh: true,
    noViews: false,
  });

  return res.data;
}

async function fetchFormData(API) {
  const res = await API.get('/lists/LookupItems?dataSource=rvcustdatascope');
  return res.data;
}

// Decode Base64 and extract HTML
function decodeHTML(base64) {
    const decoded = atob(base64);
    const match = decoded.match(/<html[\s\S]*<\/html>/i);
    return match ? match[0] : '';
}

// Extract title and body from HTML
function extractTitleAndBody(html) {
    const temp = document.createElement("div");
    temp.innerHTML = html;

    // first span is title
    const titleSpan = temp.querySelector("span");
    const title = titleSpan ? titleSpan.textContent : ""

    // second span is body
    const spans = temp.querySelectorAll("span");
    let body = "";
    
    for (let i = 1; i < spans.length; i++) {
      const text = spans[i].textContent || "";
      // Trim and append newlines
      body += text.trim() + "\n\n";
    }
    body = body.replace(/\n/g, "<br/>").trim();
    return { title, body };
}

export function AppDataProvider({ children }) {
  const { API, ready } = useApi();

  const textQuery = useQuery({
    queryKey: ['textData'],
    queryFn: () => fetchTextData(API),
    enabled: ready, // wait for token to load
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 60, // 1 hour
    retry: 1,
    suspense: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Memoize transformed data
  const textData = useMemo(() => {
    if (!textQuery.data) return [];
    return textQuery.data.map(doc => ({
      select: doc.SelectStatementTXT?.content
        ? extractTitleAndBody(decodeHTML(doc.SelectStatementTXT.content))
        : { title: "", body: "" },
      sort: doc.SortStatementTXT?.content
        ? extractTitleAndBody(decodeHTML(doc.SortStatementTXT.content))
        : { title: "", body: "" },
    }));
  }, [textQuery.data]);

  const formQuery = useQuery({
    queryKey: ['formData'],
    queryFn: () => fetchFormData(API),
    enabled: ready, // wait for token to load
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 60, // 1 hour
    retry: 1,
    suspense: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const formData = useMemo(() => {
    if (!formQuery.data) return {};
    
    return formQuery.data.reduce((acc, item) => {
      const type = item.Type || "Unknown";
      if (!acc[type]) acc[type] = [];
      acc[type].push({
        id: item["@unid"],
        description: item.Description || "",
      });
      return acc;
    }, {});
  }, [formQuery.data]);

  return (
    <QueryErrorBoundary fallback={"Failed to load app data."}>
        <AppDataContext.Provider value={{
          textData: textData ?? [], 
          isLoading: textQuery.isLoading, 
          isFetching: textQuery.isFetching, 
          refetch: textQuery.refetch,
          
          formAPIData: formData ?? {},
          isFormLoading: formQuery.isLoading,
          isFormFetching: formQuery.isFetching,
          refetchForm: formQuery.refetch,
        }}>
            {children}
        </AppDataContext.Provider>
    </QueryErrorBoundary>
  );
}

export function useAppData() {
  return useContext(AppDataContext);
}
